import styles from './header.module.scss';
import { useAppSelector, useAppDispatch } from '@app/core/hooks/redux-hooks';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { DropdownMenu } from '@app/shared/components/dropdown/DropdownMenu';
import { RootAppRoutes } from '@app/shared/enums/root-app-routes';
import SvgLogout from '@app/shared/icons/Logout';
import env from '@environment';
import { Avatar } from '@mui/material';
import Button from '@app/shared/components/button/Button';
import { FC, useEffect, useState } from 'react';
import { l } from '@locale/setupI18n';
import { FeatureFlags, getFeatureFlag } from '@app/core/feature-flags/feature-flags';
import * as notificationActions from '@app/core/store/notification-slice';
import { SeverityType } from '@app/shared/mui-components/alert/AlertTypes';
import { useFeatureFlag } from '@app/core/hooks/useFeatureFlag';
import { FeatureFlag } from '@core/feature-flags/feature-flag-enums';
import { FeatureFlagsModal } from '@app/shared/components/feature-flags-modal/FeatureFlagsModal';

export const UserProfileMenu: FC = (): JSX.Element => {
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const dispatch = useAppDispatch();
  const PARENT_CLASSNAME = 'header';
  const isDevToolsEnabled = getFeatureFlag(FeatureFlags.EraDevTools);
  const isFeatureFlagModalEnabled = useFeatureFlag(FeatureFlag.FeatureFlagModal);
  const [isFeatureFlagsModalOpen, setIsFeatureFlagsModalOpen] = useState(false);

  const extractUserInitials = (userName: string) =>
    userName
      .split(' ')
      .map((element) => element[0])
      .join('');

  const [state, setState] = useState(() => ({
    userInitials: '',
    userEmail: '',
    userName: '',
  }));

  const { versionTag, commitId } = useAppSelector((state) => state.uiValues);

  useEffect(() => {
    setState({
      userInitials: extractUserInitials(userInfo.fullUserName),
      userEmail: userInfo.email,
      userName: userInfo.fullUserName,
    });
  }, [userInfo]);

  const handleCopyCommitHash = async () => {
    if (commitId) {
      await navigator.clipboard.writeText(commitId);
      dispatch(
        notificationActions.showNotificationSnackbar({
          message: 'Hash Copied!',
          severity: SeverityType.success,
          autoHide: true,
        })
      );
    }
  };

  return (
    <DropdownMenu
      ariaLabel="User Profile"
      dropdownContent={
        <Avatar className={styles[`${PARENT_CLASSNAME}__avatar`]}>{state.userInitials}</Avatar>
      }
      ignoreReadOnly>
      <h5 className={styles[`${PARENT_CLASSNAME}__header`]}> {state.userName}</h5>
      <p title={state.userEmail} className={styles[`${PARENT_CLASSNAME}__sub-header`]}>
        {state.userEmail}
      </p>
      <div className={styles[`${PARENT_CLASSNAME}__header-link-container`]}>
        <a
          href={env.giveFeedbackUrl}
          rel="noreferrer"
          target="_blank"
          className={styles[`${PARENT_CLASSNAME}__feedback-link`]}>
          {l('_GiveFeedback')}
        </a>
        <br />
        <a
          href={env.reportIssueUrl}
          rel="noreferrer"
          target="_blank"
          className={styles[`${PARENT_CLASSNAME}__header-link`]}>
          {l('_ReportAnIssue')}
        </a>
        {isFeatureFlagModalEnabled && (
          <>
            <br />
            <Button
              appearance={ButtonAppearance.LINK}
              onClick={() => setIsFeatureFlagsModalOpen(true)}
              className={styles[`${PARENT_CLASSNAME}__header-link`]}>
              {l('_ConfigureFeatureFlags')}
            </Button>
          </>
        )}
        {isDevToolsEnabled && commitId && (
          <>
            <br />
            <Button
              appearance={ButtonAppearance.LINK}
              onClick={handleCopyCommitHash}
              className={styles[`${PARENT_CLASSNAME}__header-link`]}
              title={commitId}>
              {commitId}
            </Button>
          </>
        )}
      </div>
      <Button
        to={`/${RootAppRoutes.Logout}`}
        className={styles[`${PARENT_CLASSNAME}__logout`]}
        startIcon={<SvgLogout width={16} />}
        appearance={ButtonAppearance.DEFAULT_SECONDARY}
        ignoreReadOnly>
        {l('_LogOut')}
      </Button>

      {versionTag && (
        <div className={styles[`${PARENT_CLASSNAME}__version-tag`]}>
          {l('_EquityRewardAnalyticsVersion', { version: versionTag })}
        </div>
      )}

      {isFeatureFlagsModalOpen && (
        <FeatureFlagsModal onCloseButtonClick={() => setIsFeatureFlagsModalOpen(false)} />
      )}
    </DropdownMenu>
  );
};
